/**
 * AnimatedTitle
 * ─────────────
 * Split text animation for headings with curtain reveal effect.
 * Uses GSAP for character-by-character animation.
 */

import { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { VVS_MOTION, prefersReducedMotion, getIntensity } from './tokens';

gsap.registerPlugin(ScrollTrigger);

export default function AnimatedTitle({
  children,
  as: Component = 'h2',
  className = '',
  style = {},
  delay = 0,
  scrollTrigger = true,
  animation = 'curtain', // 'curtain' | 'wave' | 'fade'
  ...props
}) {
  const containerRef = useRef(null);
  const charsRef = useRef([]);
  const [isReady, setIsReady] = useState(false);
  
  // Split text into characters
  const text = typeof children === 'string' ? children : '';
  const chars = text.split('');
  
  useEffect(() => {
    if (!containerRef.current || prefersReducedMotion()) {
      setIsReady(true);
      return;
    }
    
    const intensity = getIntensity();
    const charElements = charsRef.current.filter(Boolean);
    
    if (charElements.length === 0) {
      setIsReady(true);
      return;
    }
    
    // Initial state
    gsap.set(charElements, { opacity: 0 });
    setIsReady(true);
    
    // Animation config based on type
    let animConfig = {};
    
    switch (animation) {
      case 'curtain':
        animConfig = {
          y: 80 * intensity,
          opacity: 0,
          rotateX: 45 * intensity,
          transformOrigin: "50% 100%",
        };
        break;
      case 'wave':
        animConfig = {
          y: 60 * intensity,
          opacity: 0,
          scale: 0.8,
        };
        break;
      case 'fade':
      default:
        animConfig = {
          opacity: 0,
          y: 20 * intensity,
        };
    }
    
    const ctx = gsap.context(() => {
      const tween = gsap.from(charElements, {
        ...animConfig,
        duration: VVS_MOTION.slow,
        ease: VVS_MOTION.wave,
        stagger: VVS_MOTION.staggerFast,
        delay,
        scrollTrigger: scrollTrigger ? {
          trigger: containerRef.current,
          start: VVS_MOTION.triggerStart,
          toggleActions: "play none none reverse",
        } : undefined,
      });
      
      return tween;
    }, containerRef);
    
    return () => ctx.revert();
  }, [text, delay, scrollTrigger, animation]);
  
  // If reduced motion or no text, render simple version
  if (prefersReducedMotion() || !text) {
    return (
      <Component className={className} style={style} {...props}>
        {children}
      </Component>
    );
  }
  
  return (
    <Component
      ref={containerRef}
      className={className}
      style={{
        ...style,
        visibility: isReady ? 'visible' : 'hidden',
      }}
      aria-label={text}
      {...props}
    >
      {chars.map((char, i) => (
        <span
          key={i}
          ref={el => charsRef.current[i] = el}
          style={{
            display: 'inline-block',
            whiteSpace: char === ' ' ? 'pre' : 'normal',
            willChange: 'transform, opacity',
          }}
          aria-hidden="true"
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </Component>
  );
}
